// ---------------------------------------------------------------------------
// Conta › Alterar minha senha — `PUT /users/me/password` (handoff 0097 §3).
//
// Autenticada, SEM gate de RBAC: é a conta do próprio usuário. A rota de admin
// (`PUT /users/:id`, em pages/EditUser) exige `iam:user:update` (era
// `iam:user:create` até o RBAC por verbo do handoff 0110).
//
// Invariante que define o desfecho da tela: sucesso REVOGA TODAS AS SESSÕES,
// inclusive a corrente. Não existe "trocar a senha e continuar navegando" — o
// 200 é sempre seguido de encerramento local + login. Por isso o aviso aparece
// ANTES do submit.
//
// Erros: 422 sempre vem com `field` → inline (FC4 §B.3), nunca toast. O 429 do
// rate limit (10 tentativas / 10 min) é toast, sem retry automático.
// ---------------------------------------------------------------------------

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { changeMyPassword } from '../../../services/users/usersApi';
import { endSession } from '../../../store/modules/auth/sessionCleanup';
import { notifyApiError } from '../../../utils/feedback';

import FormField from '../../../components/Form/FormField';
import Loading from '../../../components/Loading';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import { Section, SectionTitle, SectionBody } from '../../../styles/layout/Section';
import { PrimaryButton, GhostButton } from '../../../styles/ui/Button';

import {
   FormStack,
   WarningNote,
   WarningIcon,
   FormActions,
} from './styled';

const EMPTY_FORM = {
   current_password: '',
   new_password: '',
   new_password_confirm: '',
};

// Espelha os limites do backend (0097 §3) para poupar um round-trip óbvio.
// Não substitui a validação do servidor — apenas antecipa o erro trivial.
const PASSWORD_MIN = 6;
const PASSWORD_MAX = 50;

function validateLocally(form) {
   const errors = {};

   if (!form.current_password) {
      errors.current_password = 'Informe sua senha atual.';
   }

   if (!form.new_password) {
      errors.new_password = 'Informe a nova senha.';
   } else if (
      form.new_password.length < PASSWORD_MIN ||
      form.new_password.length > PASSWORD_MAX
   ) {
      errors.new_password = `A senha precisa ter entre ${PASSWORD_MIN} e ${PASSWORD_MAX} caracteres.`;
   } else if (form.new_password === form.current_password) {
      errors.new_password = 'A nova senha precisa ser diferente da atual.';
   }

   if (!form.new_password_confirm) {
      errors.new_password_confirm = 'Confirme a nova senha.';
   } else if (form.new_password_confirm !== form.new_password) {
      errors.new_password_confirm = 'A confirmação não confere.';
   }

   return errors;
}

export default function ChangePassword() {
   const navigate = useNavigate();
   const dispatch = useDispatch();

   const [form, setForm] = useState(EMPTY_FORM);
   const [fieldErrors, setFieldErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);

   const handleChange = (field) => (event) => {
      const { value } = event.target;
      setForm((prev) => ({ ...prev, [field]: value }));
      // Limpa o erro do campo que está sendo corrigido — inclusive o que veio
      // do backend (ex.: `invalid_current_password`).
      setFieldErrors((prev) => {
         if (!prev[field]) return prev;
         const next = { ...prev };
         delete next[field];
         return next;
      });
   };

   const handleSubmit = async (event) => {
      event.preventDefault();
      if (isSubmitting) return;

      const localErrors = validateLocally(form);
      if (Object.keys(localErrors).length > 0) {
         setFieldErrors(localErrors);
         return;
      }

      setFieldErrors({});
      setIsSubmitting(true);

      try {
         await changeMyPassword({
            currentPassword: form.current_password,
            newPassword: form.new_password,
            newPasswordConfirm: form.new_password_confirm,
         });

         toast.success(
            'Senha alterada com sucesso. Entre novamente com a nova senha.'
         );
         // A sessão já morreu no backend — encerrar localmente é o único
         // desfecho possível. Sem `finally` liberando o submit: a tela sai.
         endSession(dispatch, { delayMs: 1200 });
      } catch (err) {
         // 422 vem com `field` → inline. `notifyApiError` devolve null nesse
         // caso e não emite toast (FC4 §B.3).
         if (err?.fieldErrors && Object.keys(err.fieldErrors).length > 0) {
            setFieldErrors(err.fieldErrors);
         }
         notifyApiError(err);
         setIsSubmitting(false);
      }
   };

   return (
      <Container>
         <Loading isLoading={isSubmitting} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Alterar minha senha</PageTitle>
               <PageSubtitle>
                  Troque a senha da sua conta de acesso ao sistema.
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <Section>
            <SectionTitle>Nova senha</SectionTitle>

            <SectionBody>
               <WarningNote>
                  <WarningIcon aria-hidden="true">⚠️</WarningIcon>
                  <span>
                     Ao alterar a senha, <strong>você será desconectado de
                     todos os dispositivos</strong>, inclusive deste. Será
                     preciso entrar novamente com a nova senha.
                  </span>
               </WarningNote>

               <FormStack onSubmit={handleSubmit} noValidate>
                  <FormField
                     label="Senha atual"
                     name="current_password"
                     type="password"
                     autoComplete="current-password"
                     value={form.current_password}
                     onChange={handleChange('current_password')}
                     error={fieldErrors.current_password}
                     disabled={isSubmitting}
                  />

                  <FormField
                     label="Nova senha"
                     name="new_password"
                     type="password"
                     autoComplete="new-password"
                     value={form.new_password}
                     onChange={handleChange('new_password')}
                     error={fieldErrors.new_password}
                     hint={`Entre ${PASSWORD_MIN} e ${PASSWORD_MAX} caracteres.`}
                     disabled={isSubmitting}
                  />

                  <FormField
                     label="Confirmar nova senha"
                     name="new_password_confirm"
                     type="password"
                     autoComplete="new-password"
                     value={form.new_password_confirm}
                     onChange={handleChange('new_password_confirm')}
                     error={fieldErrors.new_password_confirm}
                     disabled={isSubmitting}
                  />

                  <FormActions>
                     <GhostButton
                        type="button"
                        onClick={() => navigate(-1)}
                        disabled={isSubmitting}
                     >
                        Cancelar
                     </GhostButton>

                     {/* Escreve no banco → azul (FC7 §G.1). */}
                     <PrimaryButton type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Alterando...' : 'Alterar senha'}
                     </PrimaryButton>
                  </FormActions>
               </FormStack>
            </SectionBody>
         </Section>
      </Container>
   );
}
